/**
 * Phase 7 Commit J. Sticky bottom action bar with running-total summary.
 *
 * Replaces the inline `<div class="sbw-actions">` button rows on most
 * steps. Anchors the booking value (selected service, total, $X
 * remaining) so the customer sees the commitment grow as they progress
 * — a CRO move (loss aversion + sunk-cost) backed by ad-checkout
 * research. Rendered absolutely-positioned at the bottom of `.sbw-stage`.
 *
 * Disabled state: `disabled=true` greys out the primary CTA without
 * hiding the bar — the summary stays visible so the user knows what
 * they're committing to once they make the missing pick.
 */

import type { ComponentChildren } from 'preact';
import { ArrowIcon, BackIcon, LockIcon } from '../icons';

interface Props {
  /** Headline summary line ("Knotless · Bra-length"). */
  summary: ComponentChildren;
  /** Smaller second line under the summary ("6h · with Jalia or any"). */
  sub?: ComponentChildren;
  /** Primary CTA label. */
  primary: string;
  /** Optional total ("$320") rendered in the primary button. */
  total?: number | string;
  /** Currency symbol — defaults to $. */
  currencySymbol?: string;
  /** Use brand color (vs default ink) for the primary button. */
  brand?: boolean;
  /** Show a back button on the left of the action group. */
  onBack?: () => void;
  /** Primary CTA handler. */
  onContinue?: () => void;
  /** Disable the primary CTA (e.g. nothing selected yet). */
  disabled?: boolean;
  /** Show a small lock icon on the primary button (Pay step). */
  showLock?: boolean;
  /** Submitting state — disables and swaps label. */
  submitting?: boolean;
}

export function RunningBar({
  summary,
  sub,
  primary,
  total,
  currencySymbol = '$',
  brand = false,
  onBack,
  onContinue,
  disabled,
  showLock,
  submitting,
}: Props) {
  const totalDisplay =
    typeof total === 'number' ? `${currencySymbol}${total}` : total ?? null;
  return (
    <div class="sbw-runbar">
      <div class="sbw-runbar-summary">
        <div class="sbw-runbar-line">{summary}</div>
        {sub && <div class="sbw-runbar-sub">{sub}</div>}
      </div>
      <div class="sbw-runbar-actions">
        {onBack && (
          <button
            type="button"
            class="sbw-btn sbw-btn--ghost"
            onClick={onBack}
            disabled={submitting}
            aria-label="Go back"
          >
            <BackIcon />
          </button>
        )}
        <button
          type="button"
          class={`sbw-btn ${brand ? 'sbw-btn--brand' : 'sbw-btn--primary'}`}
          onClick={onContinue}
          disabled={disabled || submitting}
        >
          {submitting ? 'One sec…' : primary}
          {showLock && !submitting && <LockIcon />}
          {totalDisplay !== null && !submitting && (
            <span class="sbw-btn-price">{totalDisplay}</span>
          )}
          {!showLock && !submitting && <ArrowIcon />}
        </button>
      </div>
    </div>
  );
}
